
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import { columnTitleStyles, dividerColor } from './footer-theme';
import { ifProp, theme } from 'styled-tools';
import Collapse from 'src/components/core/collapse';
import React, { ReactElement, ReactNode, useCallback, useState } from 'react';
import Svg from 'src/components/core/svg';
import caretIcon from 'src/assets/svg/caret.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode,
  id: string,
  title: string
};

/**
 * Chevron size.
 *
 * `Svg` forwards `size` to the dom, so it has to be a plain string: an
 * interpolation built with `units` would reach react as a function.
 */

const chevronSize = '32px';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  border-bottom: 1px solid ${dividerColor};
`;

/**
 * `Heading` styled component.
 *
 * The disclosure pattern wraps the trigger in a heading so the footer sections
 * stay reachable by heading navigation on mobile too.
 */

const Heading = styled.h2`
  margin: 0;
`;

/**
 * `Trigger` styled component.
 */

const Trigger = styled.button`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  color: ${color('white')};
  cursor: pointer;
  display: flex;
  gap: ${units(3)};
  justify-content: space-between;
  outline: none;
  padding: ${units(2.5)} 0;
  text-align: left;
  transition: color ${theme('animations.fastTransition')};
  width: 100%;

  &:focus-visible,
  &:hover {
    color: ${color.transparentize('white', 0.7)};
  }

  &:focus-visible span {
    text-decoration: underline;
  }
`;

/**
 * `Title` styled component.
 */

const Title = styled.span`
  ${columnTitleStyles}

  color: inherit;
`;

/**
 * `Chevron` styled component.
 */

const Chevron = styled(Svg)<{ isOpen: boolean }>`
  color: inherit;
  flex: none;
  transition: transform ${theme('animations.defaultTransition')};

  ${ifProp('isOpen', css`
    transform: rotate(180deg);
  `)}
`;

/**
 * `Panel` styled component.
 */

const Panel = styled.div`
  padding-bottom: ${units(2)};
`;

/**
 * `FooterAccordion` component.
 *
 * The mobile rendering of a footer column. Collapsed content is taken out of
 * the tab order and out of the accessibility tree by `Collapse`, which hides
 * it with `display: none` once the animation ends.
 */

const FooterAccordion = ({ children, id, title }: Props): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const handleToggle = useCallback(() => {
    setIsOpen(value => !value);
  }, []);

  return (
    <Wrapper>
      <Heading>
        <Trigger
          aria-controls={id}
          aria-expanded={isOpen}
          onClick={handleToggle}
          type={'button'}
        >
          <Title>
            {title}
          </Title>

          <Chevron
            icon={caretIcon}
            isOpen={isOpen}
            size={chevronSize}
          />
        </Trigger>
      </Heading>

      <Collapse
        id={id}
        isOpen={isOpen}
      >
        <Panel>
          {children}
        </Panel>
      </Collapse>
    </Wrapper>
  );
};

/**
 * Export `FooterAccordion` component.
 */

export default FooterAccordion;
